/**
 * Content Detection Utilities
 *
 * Functions to detect and group content by type with counts.
 * Supports multiple items of the same type per case.
 */

import type { ModerationCase, Content } from '@moderation/domain/ModerationCase';

export type ContentType = 'text' | 'image' | 'video' | 'audio';

export interface TextMessage {
  id: string;
  label: string;
  content: string;
  contentId: string;
}

export interface MediaItem {
  id: string;
  url: string;
  contentId: string;
  type: ContentType;
}

/**
 * Returns a map of available content types with their counts
 *
 * @example
 * ```ts
 * const contentTypes = getAvailableContentTypes(moderationCase);
 * // Map { 'text' => 3, 'image' => 4 }
 * ```
 */
export function getAvailableContentTypes(moderationCase: ModerationCase | null | undefined): Map<ContentType, number> {
  const typeCounts = new Map<ContentType, number>();

  if (!moderationCase?.report?.contents) {
    return typeCounts;
  }

  const contents = moderationCase.report.contents;

  // Count each content type
  contents.forEach(content => {
    const contentType = content.contentType.toLowerCase() as ContentType;

    // Only count valid content types with data
    if (content.data && ['text', 'image', 'video', 'audio'].includes(contentType)) {
      const currentCount = typeCounts.get(contentType) || 0;

      typeCounts.set(contentType, currentCount + 1);
    }
  });

  return typeCounts;
}

/**
 * Gets all contents of a specific type
 * Returns ALL items, not just the first one
 */
export function getContentsByType(moderationCase: ModerationCase | null | undefined, contentType: ContentType): Content[] {
  if (!moderationCase?.report?.contents) {
    return [];
  }

  return moderationCase.report.contents.filter(content => content.contentType.toLowerCase() === contentType && content.data);
}

/**
 * Transforms text contents into labeled messages array
 * Each text item is labeled as "Message 1", "Message 2", etc.
 *
 * @example
 * ```ts
 * const messages = getTextMessages(moderationCase);
 * // [
 * //   { id: 'content-1', label: 'Message 1', content: '...', contentId: 'content-1' },
 * //   { id: 'content-2', label: 'Message 2', content: '...', contentId: 'content-2' }
 * // ]
 * ```
 */
export function getTextMessages(moderationCase: ModerationCase | null | undefined): TextMessage[] {
  const textContents = getContentsByType(moderationCase, 'text');

  return textContents.map((content, index) => ({
    id: content.contentId,
    label: textContents.length > 1 ? `Message ${index + 1}` : 'Message',
    content: content.data,
    contentId: content.contentId,
  }));
}

/**
 * Transforms image contents into image items array
 * Returns ALL images, not just the first one
 *
 * @example
 * ```ts
 * const images = getImageItems(moderationCase);
 * // [
 * //   { id: 'img-1', url: 'https://...', contentId: 'content-4', type: 'image' },
 * //   { id: 'img-2', url: 'https://...', contentId: 'content-5', type: 'image' }
 * // ]
 * ```
 */
export function getImageItems(moderationCase: ModerationCase | null | undefined): MediaItem[] {
  const imageContents = getContentsByType(moderationCase, 'image');

  return imageContents.map(content => ({
    id: content.contentId,
    url: content.data,
    contentId: content.contentId,
    type: 'image' as const,
  }));
}

/**
 * Gets the total count of items for a specific content type
 */
export function getContentTypeCount(moderationCase: ModerationCase | null | undefined, contentType: ContentType): number {
  const contentTypes = getAvailableContentTypes(moderationCase);

  return contentTypes.get(contentType) || 0;
}

/**
 * Checks if a specific content type exists in the case
 */
export function hasContentType(moderationCase: ModerationCase | null | undefined, contentType: ContentType): boolean {
  return getContentTypeCount(moderationCase, contentType) > 0;
}
